import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../contexts/NotificationContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  ArrowLeft, Bell, BellOff, Plus, Trash2, Clock, 
  Calendar, AlertCircle, CheckCircle, Pill, Apple
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const RemindersPage = () => {
  const navigate = useNavigate();
  const { 
    isSupported, 
    permission, 
    requestPermission, 
    reminders, 
    addReminder, 
    removeReminder, 
    toggleReminder,
    sendNotification 
  } = useNotifications();

  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [newReminder, setNewReminder] = useState({
    title: '',
    body: '',
    time: '09:00',
    frequency: 'daily',
    type: 'supplement',
    days: [],
  });

  const reminderTypes = [
    { value: 'supplement', label: 'Supplement', icon: Pill },
    { value: 'medication', label: 'Medication', icon: Pill },
    { value: 'food', label: 'Food/Meal', icon: Apple },
    { value: 'other', label: 'Other', icon: Bell },
  ];

  const weekDays = [
    { value: 'sunday', label: 'Sun' },
    { value: 'monday', label: 'Mon' },
    { value: 'tuesday', label: 'Tue' },
    { value: 'wednesday', label: 'Wed' },
    { value: 'thursday', label: 'Thu' },
    { value: 'friday', label: 'Fri' },
    { value: 'saturday', label: 'Sat' },
  ];

  const handleRequestPermission = async () => {
    const granted = await requestPermission();
    if (granted) {
      toast.success('Notifications enabled!');
      // Send a test notification
      sendNotification('Notifications Enabled!', {
        body: 'You will now receive reminder notifications from Calm Pathways.',
      });
    } else {
      toast.error('Notification permission denied. Please enable in browser settings.');
    }
  };

  const handleAddReminder = () => {
    if (!newReminder.title.trim()) {
      toast.error('Please enter a reminder title');
      return;
    }

    if (newReminder.frequency === 'weekly' && newReminder.days.length === 0) {
      toast.error('Please select at least one day for weekly reminders');
      return;
    }

    addReminder(newReminder);
    toast.success('Reminder added!');
    setShowAddForm(false);
    setNewReminder({
      title: '',
      body: '',
      time: '09:00',
      frequency: 'daily',
      type: 'supplement',
      days: [],
    });
  };

  const handleDeleteReminder = () => {
    if (deleteId) {
      removeReminder(deleteId);
      toast.success('Reminder deleted');
      setDeleteId(null);
    }
  };

  const toggleDay = (day) => {
    setNewReminder(prev => ({
      ...prev,
      days: prev.days.includes(day) 
        ? prev.days.filter(d => d !== day)
        : [...prev.days, day]
    }));
  };

  if (!isSupported) {
    return (
      <div className="space-y-8" data-testid="reminders-page">
        <Button 
          onClick={() => navigate('/settings')} 
          variant="ghost" 
          className="text-primary hover:text-primary/80"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Settings
        </Button>

        <Card className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-nunito font-bold text-[#2D2D2D] mb-2">
            Notifications Not Supported
          </h2>
          <p className="text-[#57534E]">
            Your browser does not support push notifications. Please try using a modern browser 
            like Chrome, Firefox, or Safari.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="reminders-page">
      <Button 
        onClick={() => navigate('/settings')} 
        variant="ghost" 
        className="text-primary hover:text-primary/80"
        data-testid="back-button"
      >
        <ArrowLeft className="w-5 h-5 mr-2" />
        Back to Settings
      </Button>

      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl p-8">
        <div className="flex items-center gap-4 mb-2">
          <Bell className="w-10 h-10 text-primary" strokeWidth={1.5} />
          <h1 className="text-3xl font-nunito font-bold text-[#2D2D2D]">
            Reminders
          </h1>
        </div>
        <p className="text-[#57534E]">
          Set up reminders for supplements, medications, and meals
        </p>
      </div>

      {/* Permission Status */}
      {permission !== 'granted' && (
        <Card className="bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <BellOff className="w-8 h-8 text-amber-500 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-[#2D2D2D] mb-2">
                Enable Notifications
              </h3>
              <p className="text-[#57534E] text-sm mb-4">
                To receive reminders, you need to enable notifications in your browser.
              </p>
              <Button 
                onClick={handleRequestPermission}
                className="bg-primary text-white hover:bg-primary/90 rounded-full"
                data-testid="enable-notifications-btn"
              >
                <Bell className="w-4 h-4 mr-2" />
                Enable Notifications
              </Button>
            </div>
          </div>
        </Card>
      )}

      {permission === 'granted' && (
        <Card className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-800 font-medium">Notifications are enabled</span>
          </div>
        </Card>
      )}

      {/* Add Reminder Form */}
      {showAddForm ? (
        <Card className="bg-white rounded-xl border border-stone-100 shadow-sm p-6">
          <h3 className="text-lg font-nunito font-bold text-[#2D2D2D] mb-4">
            New Reminder
          </h3>
          
          <div className="space-y-4">
            {/* Type Selection */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D] block mb-2">Type</label>
              <div className="flex flex-wrap gap-2">
                {reminderTypes.map(type => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => setNewReminder(prev => ({ ...prev, type: type.value }))}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full font-medium transition-colors ${
                        newReminder.type === type.value
                          ? 'bg-primary text-white'
                          : 'bg-stone-100 text-[#57534E] hover:bg-stone-200'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {type.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D] block mb-2">
                Title *
              </label>
              <Input
                value={newReminder.title}
                onChange={(e) => setNewReminder(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Take Vitamin D"
                className="rounded-lg"
                data-testid="reminder-title-input"
              />
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D] block mb-2">
                Description (optional)
              </label>
              <Input
                value={newReminder.body}
                onChange={(e) => setNewReminder(prev => ({ ...prev, body: e.target.value }))}
                placeholder="e.g., 2000 IU with breakfast"
                className="rounded-lg"
              />
            </div>

            {/* Time */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D] block mb-2">
                Time
              </label>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#A8A29E]" />
                <Input
                  type="time"
                  value={newReminder.time}
                  onChange={(e) => setNewReminder(prev => ({ ...prev, time: e.target.value }))}
                  className="rounded-lg w-auto"
                  data-testid="reminder-time-input"
                />
              </div>
            </div>

            {/* Frequency */}
            <div>
              <label className="text-sm font-medium text-[#2D2D2D] block mb-2">
                Frequency
              </label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'daily', label: 'Every Day' },
                  { value: 'weekly', label: 'Specific Days' },
                  { value: 'once', label: 'One Time' },
                ].map(freq => (
                  <button
                    key={freq.value}
                    onClick={() => setNewReminder(prev => ({ ...prev, frequency: freq.value }))}
                    className={`px-4 py-2 rounded-full font-medium transition-colors ${
                      newReminder.frequency === freq.value
                        ? 'bg-secondary text-secondary-foreground'
                        : 'bg-stone-100 text-[#57534E] hover:bg-stone-200'
                    }`}
                  >
                    {freq.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Day Selection for Weekly */}
            {newReminder.frequency === 'weekly' && (
              <div>
                <label className="text-sm font-medium text-[#2D2D2D] block mb-2">
                  Select Days
                </label>
                <div className="flex flex-wrap gap-2">
                  {weekDays.map(day => (
                    <button
                      key={day.value}
                      onClick={() => toggleDay(day.value)}
                      className={`w-10 h-10 rounded-full font-medium text-sm transition-colors ${
                        newReminder.days.includes(day.value)
                          ? 'bg-primary text-white'
                          : 'bg-stone-100 text-[#57534E] hover:bg-stone-200'
                      }`}
                    >
                      {day.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleAddReminder}
                className="bg-primary text-white hover:bg-primary/90 rounded-full"
                data-testid="save-reminder-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Reminder
              </Button>
              <Button
                onClick={() => setShowAddForm(false)}
                variant="outline"
                className="rounded-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      ) : (
        <Button
          onClick={() => setShowAddForm(true)}
          className="bg-primary text-white hover:bg-primary/90 rounded-full"
          data-testid="add-reminder-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Reminder
        </Button>
      )}

      {/* Reminders List */}
      <div className="space-y-4">
        <h3 className="text-lg font-nunito font-bold text-[#2D2D2D]">
          Your Reminders ({reminders.length})
        </h3>

        {reminders.length === 0 ? (
          <Card className="bg-white rounded-xl border border-stone-100 p-8 text-center">
            <Bell className="w-12 h-12 text-[#A8A29E] mx-auto mb-4" />
            <p className="text-[#57534E]">No reminders yet</p>
            <p className="text-sm text-[#A8A29E] mt-1">
              Add a reminder to get started
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {reminders.map(reminder => {
              const TypeIcon = reminderTypes.find(t => t.value === reminder.type)?.icon || Bell;
              return (
                <Card 
                  key={reminder.id}
                  className={`bg-white rounded-xl border shadow-sm p-4 transition-opacity ${
                    reminder.enabled ? 'border-stone-100' : 'border-stone-200 opacity-60'
                  }`}
                  data-testid={`reminder-${reminder.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-full ${
                        reminder.enabled ? 'bg-primary/10' : 'bg-stone-100'
                      }`}>
                        <TypeIcon className={`w-5 h-5 ${
                          reminder.enabled ? 'text-primary' : 'text-[#A8A29E]'
                        }`} />
                      </div>
                      <div>
                        <p className="font-semibold text-[#2D2D2D]">{reminder.title}</p>
                        <div className="flex items-center gap-3 text-sm text-[#57534E]">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {reminder.time}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {reminder.frequency === 'daily' ? 'Daily' : 
                             reminder.frequency === 'weekly' ? reminder.days?.join(', ') : 
                             'One time'}
                          </span>
                        </div>
                        {reminder.body && (
                          <p className="text-xs text-[#A8A29E] mt-1">{reminder.body}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleReminder(reminder.id)}
                        className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                          reminder.enabled ? 'bg-primary justify-end' : 'bg-stone-300 justify-start'
                        }`}
                        data-testid={`toggle-reminder-${reminder.id}`}
                      >
                        <div className="w-4 h-4 bg-white rounded-full" />
                      </button>
                      <button
                        onClick={() => setDeleteId(reminder.id)}
                        className="p-2 text-[#A8A29E] hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                        data-testid={`delete-reminder-${reminder.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Reminder?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The reminder will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteReminder}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default RemindersPage;
